import { interaction, logger } from "@origin-digital/reporting-client";
import {
  eventListener,
  IAppDiagnostics,
  TOPICS,
} from "@origin-digital/event-dispatcher";
import { getEnv } from "./helper";
import initialize from "./intercom";
import { LOG_TAG } from "./logTag";

export interface IIntercomSettings {
  user_id?: string;
  hide_default_launcher?: boolean;
  custom_launcher_selector?: string;
  timer?: number;
}

function install(
  appId: string,
  backends: string[],
  userHash?: string,
  settings?: IIntercomSettings
): void {
  createSettingsInWindow(appId, backends, userHash, settings);
  initialize(appId);
  addAnalyticsOnShow();
}

/* Initialise intercom. Delays intialisation until after appReady if it's configured
 ** Has fallbacks to ensure that intercom is always correctly initialised if this function is called
 * Guaranteed to be called after 3 seconds, if not before */
export function initIntercom(
  backends: string[],
  userHash?: string,
  settings?: IIntercomSettings
): void {
  const appId = getEnv() === "prod" ? "hs2d6jw6" : "pllf553u";

  const timer = window?.intercomSettings?.timer;
  // Don't double initialise
  const isInit = window?.intercomSettings?.app_id && window?.Intercom;
  if (isInit) {
    if (timer) clearTimeout(timer);
    return;
  }
  // appReady may not be configured for the page, if so, fallback to document ready
  const ready = window?.oetal?.timings?.appReady
    ? window?.oetal?.timings?.appReady.counter > 0
    : document.readyState === "complete";

  if (ready) {
    install(appId, backends, userHash, settings);
    if (timer) clearTimeout(timer);
  } else {
    // Listen for document ready, in case of appReady not being setup on the page
    window.addEventListener(
      "load",
      () => {
        initIntercom(backends, userHash, settings);
      },
      false
    );

    eventListener.addListener(
      TOPICS.APP_DIAGNOSTICS,
      (event: IAppDiagnostics) => {
        if (event.payload.name === "appReady") {
          initIntercom(backends, userHash, settings);
        }
      }
    );
    if (!window.intercomSettings) window.intercomSettings = {};
    // Force initialise if timeout is reached
    if (!timer) {
      window.intercomSettings.timer = setTimeout(
        () => install(appId, backends, userHash, settings),
        3000
      );
    }
  }
}

export async function createSettingsInWindow(
  appId: string,
  backends: string[],
  userHash?: string,
  settings?: IIntercomSettings
): Promise<void> {
  const { user_id, ...rest } = settings || {};

  window.intercomSettings =
    user_id && userHash
      ? {
          app_id: appId,
          user_id,
          user_hash: userHash,
          mcid: getMarketingCloudVisitorID(),
          custom_launcher_selector: ".oe_chat_launcher",
          backend_crms: backends.join(", "),
          ...rest,
        }
      : {
          app_id: appId,
          mcid: getMarketingCloudVisitorID(),
          custom_launcher_selector: ".oe_chat_launcher",
          ...rest,
        };
}

function getMarketingCloudVisitorID(): string | null {
  try {
    const mcid = window["_satellite"]
      .getVisitorId()
      .getMarketingCloudVisitorID();
    // eslint-disable-next-line no-negated-condition
    return mcid !== null ? mcid : null;
  } catch (e) {
    if (e instanceof Error) {
      logger.error(`${LOG_TAG} failed to get MarketingCloudVisitorID`, e);
    }
    return null;
  }
}

function addAnalyticsOnShow(): void {
  if (window.Intercom) {
    window.Intercom("onShow", () => {
      interaction("click", {
        componentType: "button",
        id: "open-chat",
        parentContainer: "intercom",
        label: "open chat widget",
      });
    });
  } else {
    logger.error(`${LOG_TAG} failed to add analytics: open-chat: web`);
  }
}
